import { useCallback, useEffect, useState } from 'react';
import type { Dimension, Entry, LevelDescriptor } from '../../shared/types.js';
import type { ListEntriesQuery } from '../../shared/schemas.js';
import { api } from '../api/client.js';
import { QuickAdd } from './QuickAdd.js';
import { Filters } from './Filters.js';
import { EntryList } from './EntryList.js';
import { EntryDetail } from './EntryDetail.js';

interface Props {
  dimensions: Dimension[];
  descriptors: LevelDescriptor[];
  initialEntryId?: string | null;
}

export function CaptureView({ dimensions, descriptors, initialEntryId }: Props) {
  const [filters, setFilters] = useState<ListEntriesQuery>({});
  const [entries, setEntries] = useState<Entry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialEntryId ?? null);

  const refreshEntries = useCallback(() => {
    api
      .listEntries(filters)
      .then(setEntries)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load entries'));
  }, [filters]);

  useEffect(() => {
    refreshEntries();
  }, [refreshEntries]);

  return (
    <>
      <QuickAdd onAdded={refreshEntries} />
      {error && <p className="error">{error}</p>}
      {selectedId ? (
        <EntryDetail
          entryId={selectedId}
          dimensions={dimensions}
          descriptors={descriptors}
          onBack={() => setSelectedId(null)}
          onChanged={refreshEntries}
        />
      ) : (
        <>
          <Filters value={filters} dimensions={dimensions} onChange={setFilters} />
          <EntryList entries={entries} onOpen={setSelectedId} />
        </>
      )}
    </>
  );
}
